<template>
  <v-container fluid class="ma-0 pa-0 mt-3">
    <!-- Header con KPI principali -->
    <v-row class="ma-0 pa-2 mb-3">
      <v-col cols="12" sm="6" md="3" v-for="(kpi, i) in schede" :key="i">
        <v-card class="elevation-2">
          <v-card-text class="pa-3">
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-caption text--secondary">{{ kpi.etichetta }}</div>
                <div class="text-h5 font-weight-bold">{{ kpi.valore }}</div>
                <div class="text-caption" :class="classeTendenza(kpi.tendenza)">
                  <v-icon small :color="coloreTendenza(kpi.tendenza)">
                    {{ iconaTendenza(kpi.tendenza) }}
                  </v-icon>
                  {{ Math.abs(kpi.tendenza) }}% {{ etichettaConfronto }}
                </div>
              </div>
              <v-icon size="40" color="primary">{{ kpi.icona }}</v-icon>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filtri Dashboard -->
    <v-card class="elevation-2 mb-3">
      <v-card-text class="py-2">
        <v-row dense align="center">
          <v-col cols="6" sm="3" md="1">
            <v-select v-model="filtriGrafici.anno" :items="anniDisponibili" label="Anno" dense hide-details
              @change="caricaTuttiDati" />
          </v-col>

          <v-col cols="12" sm="9" md="3">
            <v-autocomplete v-model="filtriGrafici.cliente" :items="elencoClienti" item-text="ragSoc"
              item-value="codAnagrafica" label="Cliente" dense hide-details clearable
              no-data-text="Nessun dato disponibile" @change="caricaTuttiDati" />
          </v-col>

          <v-col cols="12" sm="6" md="3">
            <v-autocomplete v-model="filtriGrafici.operatore" :items="elencoOperatori" label="Operatore" dense
              hide-details clearable multiple small-chips no-data-text="Nessun dato disponibile"
              @change="caricaTuttiDati" />
          </v-col>

          <v-col cols="6" sm="3" md="2">
            <v-select v-model="filtriGrafici.destinazione" :items="destinazioni" label="Destinazione" dense
              hide-details @change="caricaTuttiDati" />
          </v-col>

          <v-col cols="6" sm="3" md="1">
            <v-select v-model="filtriGrafici.tipoOre" :items="tipiOre" label="Tipo ore" dense hide-details
              @change="caricaTuttiDati" />
          </v-col>

          <v-col cols="12" md="2" class="d-flex align-center">
            <v-switch v-model="filtriGrafici.soloDaFatturare" label="Solo ore da fatturare" dense hide-details
              class="mt-0" @change="caricaTuttiDati" />
            <v-spacer />
            <v-btn icon small @click="caricaTuttiDati">
              <v-icon>refresh</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Tabs per diverse visualizzazioni -->
    <v-tabs v-model="tabAttiva" background-color="primary" dark>
      <v-tab>
        <v-icon left>show_chart</v-icon>
        Andamento
      </v-tab>
      <v-tab>
        <v-icon left>pie_chart</v-icon>
        Composizione
      </v-tab>
      <v-tab>
        <v-icon left>assessment</v-icon>
        Clienti e Resa
      </v-tab>
      <v-tab>
        <v-icon left>compare_arrows</v-icon>
        Confronti
      </v-tab>
    </v-tabs>

    <v-tabs-items v-model="tabAttiva" class="mt-3" touchless>
      <!-- Tab 1: Andamento -->
      <v-tab-item>
        <tab-andamento-ore :filtri="filtriGrafici" :modalita="modalitaVisualizzazione"
          :aggiornaDati="aggiornaDati" />
      </v-tab-item>

      <!-- Tab 2: Composizione -->
      <v-tab-item>
        <tab-composizione-ore :filtri="filtriGrafici" :modalita="modalitaVisualizzazione"
          :aggiornaDati="aggiornaDati" />
      </v-tab-item>

      <!-- Tab 3: Ore dedicate ai clienti, resa e clienti a rischio -->
      <v-tab-item>
        <tab-clienti-resa :filtri="filtriGrafici" :aggiornaDati="aggiornaDati" />
      </v-tab-item>

      <!-- Tab 4: Confronti -->
      <v-tab-item>
        <tab-confronti-ore :filtri="filtriGrafici" :modalita="modalitaVisualizzazione" :aggiornaDati="aggiornaDati" />
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import { get_kpi_ore } from "@/api/api.clienti.js";
import mixinRiepilogo from "./mixinRiepilogo.js";
import TabAndamentoOre from "./TabAndamentoOre.vue";
import TabComposizioneOre from "./TabComposizioneOre.vue";
import TabClientiResa from "./TabClientiResa.vue";
import TabConfrontiOre from "./TabConfrontiOre.vue";

export default {
  name: "DashboardOre",

  components: {
    TabAndamentoOre,
    TabComposizioneOre,
    TabClientiResa,
    TabConfrontiOre
  },

  mixins: [mixinRiepilogo],

  props: {
    elencoClienti: { type: Array, default: () => [] },
    elencoOperatori: { type: Array, default: () => [] }
  },

  data: () => ({
    tabAttiva: 0,
    aggiornaDati: 0,
    modalitaVisualizzazione: 'ore', // 'importi' o 'ore'

    filtriGrafici: {
      anno: new Date().getFullYear(),
      soloDaFatturare: true,
      cliente: "",
      operatore: [],
      destinazione: "Cliente",
      tipoOre: "Lavoro"
    },

    destinazioni: [],
    tipiOre: [],
    anniDisponibili: [],

    // true se l'anno è in corso: le tendenze sono calcolate a parità di periodo
    kpiParziale: false,

    schede: [
      {
        etichetta: 'Ore Totali',
        valore: '0',
        tendenza: 0,
        icona: 'schedule',
        colore: 'success'
      },
      {
        etichetta: 'Importo Totale',
        valore: '€ 0',
        tendenza: 0,
        icona: 'euro_symbol',
        colore: 'info'
      },
      {
        etichetta: 'Ore Medie Giornaliere',
        valore: '0',
        tendenza: 0,
        icona: 'analytics',
        colore: 'warning'
      },
      {
        etichetta: 'Clienti Attivi',
        valore: '0',
        tendenza: 0,
        icona: 'people',
        colore: 'primary lighten-5'
      }
    ]
  }),

  computed: {
    etichettaConfronto() {
      const annoPrecedente = this.filtriGrafici.anno - 1;
      return this.kpiParziale ? `vs stesso periodo ${annoPrecedente}` : `vs ${annoPrecedente}`;
    }
  },

  methods: {
    async caricaDatiKPI() {
      try {
        let res = await get_kpi_ore(this.filtriGrafici);
        if (res && res.data) {
          const dati = res.data;

          this.kpiParziale = dati.parziale;

          this.schede[0].valore = dati.oreTotali.toFixed(0);
          this.schede[0].tendenza = dati.tendenzaOre || 0;

          this.schede[1].valore = '€ ' + this.$functions.numberWithCommas(dati.importoTotale.toFixed(2));
          this.schede[1].tendenza = dati.tendenzaImporto || 0;

          this.schede[2].valore = dati.oreMediaGiornaliera.toFixed(2);
          this.schede[2].tendenza = dati.tendenzaMedia || 0;

          this.schede[3].valore = dati.clientiAttivi.toString();
          this.schede[3].tendenza = dati.tendenzaClienti || 0;
        }
      } catch (error) {
        console.error("Errore caricamento KPI:", error);
      }
    },

    classeTendenza(tendenza) {
      if (tendenza > 0) return 'success--text';
      return tendenza < 0 ? 'error--text' : 'text--secondary';
    },

    coloreTendenza(tendenza) {
      if (tendenza > 0) return 'success';
      return tendenza < 0 ? 'error' : 'grey';
    },

    iconaTendenza(tendenza) {
      if (tendenza > 0) return 'trending_up';
      return tendenza < 0 ? 'trending_down' : 'trending_flat';
    },

    // Il watcher su aggiornaDati ricarica i KPI; le tab ricaricano i propri dati
    caricaTuttiDati() {
      this.aggiornaDati++;
    },

    cambiaModalita() {
      // Forza aggiornamento dei grafici quando cambia modalità
      this.aggiornaDati++;
    },

    inizializzaAnni() {
      const annoCorrente = new Date().getFullYear();
      this.anniDisponibili = [];

      for (let i = 0; i < 6; i++) {
        this.anniDisponibili.push(annoCorrente - i);
      }
    }
  },

  mounted() {
    this.destinazioni = this.getDestinazioni();
    this.tipiOre = this.getTipiOre();
    this.inizializzaAnni();
    this.caricaDatiKPI();
  },

  watch: {
    aggiornaDati() {
      this.caricaDatiKPI();
    }
  }
}
</script>
